/**
 * The one analytics number formatter (ANL-E04-S08, CMP-11.21, FR-ADM-30).
 *
 * New analytics code prints every number through here; the nine older
 * formatters elsewhere are left alone (ADM-OI-23). Two rules carry the
 * privacy and honesty promises of the panel:
 * - a value with no source prints "Not measured" with a reason, never 0
 *   (FR-VAL-14), and a true zero prints "0";
 * - a suppressed value prints "Under 10" (D5.4) or "Withheld", and so does any
 *   rate built from it. The client never computes a hidden value (AC7).
 */

export type SuppressedValue = {
  suppressed: true;
  // "complement": a cell of 10 or more withheld so a smaller one cannot be
  // worked out from a total (S23 AC10).
  reason?: "floor" | "complement";
};

export type MeasuredValue = {
  // null: no source measures this value.
  value: number | null;
  reason?: string;
  approximate?: boolean;
};

export type RatioValue = {
  numerator: number | SuppressedValue;
  denominator: number | SuppressedValue;
  reason?: string;
  approximate?: boolean;
};

export type AnalyticsValue = SuppressedValue | MeasuredValue | RatioValue;

export type ValueFormat =
  | "count"
  | "rate"
  | "dollars"
  | "averageDollars"
  | "duration";

export type PrintedValue = {
  // What the tile shows.
  text: string;
  // What a screen reader announces.
  label: string;
  // Why a value is Not measured; null otherwise.
  reason: string | null;
};

export type DeltaChipInput = {
  kind: "count" | "rate";
  current: AnalyticsValue;
  previous: AnalyticsValue | null;
  // The server's change: relative percent for a count, points for a rate.
  // Omitted when an input is suppressed (S23 AC14).
  change?: number | null;
};

const SUPPRESSION_FLOOR = 10;
export const NOT_MEASURED = "Not measured";
export const UNDER_FLOOR = `Under ${SUPPRESSION_FLOOR}`;
export const WITHHELD = "Withheld";

const NO_SOURCE_REASON = "No source measures this value yet";
const ZERO_DENOMINATOR_REASON = "Nothing to divide by in this range";
const MINUS = "−";

const MS_PER_SECOND = 1000;
const DURATION_UNITS: ReadonlyArray<readonly [string, number]> = [
  ["d", 86_400],
  ["h", 3_600],
  ["m", 60],
  ["s", 1],
];
const COMPACT_UNITS: ReadonlyArray<readonly [string, number]> = [
  ["B", 1e9],
  ["M", 1e6],
  ["k", 1e3],
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isSuppressedPart = (value: unknown): value is SuppressedValue =>
  isPlainObject(value) && value.suppressed === true;

const isRatioPart = (value: unknown): boolean =>
  typeof value === "number" || isSuppressedPart(value);

/** True for a value of one of the three shapes; anything else is malformed. */
export const isAnalyticsValue = (value: unknown): value is AnalyticsValue => {
  if (!isPlainObject(value)) return false;
  if (value.suppressed === true) return true;
  if ("value" in value) return value.value === null || typeof value.value === "number";
  return isRatioPart(value.numerator) && isRatioPart(value.denominator);
};

const isRatio = (value: AnalyticsValue): value is RatioValue => "numerator" in value;

const suppressedPartsOf = (value: AnalyticsValue): SuppressedValue[] => {
  if (isSuppressedPart(value)) return [value];
  if (!isRatio(value)) return [];
  return [value.numerator, value.denominator].filter(isSuppressedPart);
};

export const isSuppressedValue = (value: AnalyticsValue): boolean =>
  suppressedPartsOf(value).length > 0;

/** null, a non-finite number, or a ratio over 0: nothing was measured. */
export const isUnmeasuredValue = (value: AnalyticsValue): boolean => {
  if (isSuppressedValue(value)) return false;
  if (isRatio(value)) {
    const { numerator, denominator } = value as { numerator: number; denominator: number };
    return !Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0;
  }
  const { value: measured } = value as MeasuredValue;
  return measured === null || !Number.isFinite(measured);
};

const fixed = (digits: number) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const WHOLE = fixed(0);
const ONE_DECIMAL = fixed(1);
const TWO_DECIMALS = fixed(2);

/**
 * The true minus sign on negatives, and no sign on a value that rounds to 0.
 * NaN and ±Infinity measure nothing, so they print "Not measured" (FR-VAL-14).
 */
const signed = (
  value: number,
  print: (magnitude: number) => string,
  positiveSign = "",
): string => {
  if (!Number.isFinite(value)) return NOT_MEASURED;
  const text = print(Math.abs(value));
  if (!/[1-9]/.test(text)) return text;
  if (value < 0) return `${MINUS}${text}`;
  return `${positiveSign}${text}`;
};

export const formatCount = (value: number): string =>
  signed(value, (magnitude) => WHOLE.format(magnitude));

/** Compact form ("1.2k"): for chart axis ticks only, never a tile or a table. */
export const formatAxisTick = (value: number): string =>
  signed(value, (magnitude) => {
    for (let index = 0; index < COMPACT_UNITS.length; index += 1) {
      const [suffix, size] = COMPACT_UNITS[index];
      if (magnitude < size) continue;
      const scaled = Math.round((magnitude / size) * 10) / 10;
      if (scaled >= 1000 && index > 0) {
        const [largerSuffix] = COMPACT_UNITS[index - 1];
        return `1${largerSuffix}`;
      }
      return `${ONE_DECIMAL.format(scaled).replace(/\.0$/, "")}${suffix}`;
    }
    return WHOLE.format(magnitude);
  });

/** A rate in percent: one decimal, two below 1%, and a bare "0%" for zero. */
export const formatRate = (percent: number): string =>
  signed(percent, (magnitude) => {
    if (magnitude === 0) return "0%";
    if (magnitude < 1) {
      // 0.996 rounds up to 1%, so it follows the one-decimal rule ("1.0%").
      const twoDecimals = TWO_DECIMALS.format(magnitude);
      if (twoDecimals !== "1.00") return `${twoDecimals}%`;
    }
    return `${ONE_DECIMAL.format(magnitude)}%`;
  });

/** A change in a count, as relative percent ("+8.6%"). */
export const formatCountChange = (percent: number): string =>
  signed(percent, (magnitude) => `${ONE_DECIMAL.format(magnitude)}%`, "+");

/** A change in a rate, in percentage points ("+0.4 pt"). */
export const formatRateChange = (points: number): string =>
  signed(points, (magnitude) => `${ONE_DECIMAL.format(magnitude)} pt`, "+");

/** Whole dollars above $1,000 ("$2,392"); cents at or below it. */
export const formatDollars = (amount: number): string =>
  signed(amount, (magnitude) =>
    magnitude > 1000 ? `$${WHOLE.format(magnitude)}` : `$${TWO_DECIMALS.format(magnitude)}`,
  );

/** An average is always printed with cents ("$46.00"). */
export const formatAverageDollars = (amount: number): string =>
  signed(amount, (magnitude) => `$${TWO_DECIMALS.format(magnitude)}`);

/** A duration in its two largest units ("2 d 11 h", "7 m 12 s"). */
export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms)) return NOT_MEASURED;
  const total = Math.floor(Math.abs(ms) / MS_PER_SECOND);
  const index = DURATION_UNITS.findIndex(([, size]) => total >= size);
  if (index === -1) return "0 s";
  const [unit, size] = DURATION_UNITS[index];
  const parts = [`${Math.floor(total / size)} ${unit}`];
  const smaller = DURATION_UNITS[index + 1];
  if (smaller) {
    const [smallerUnit, smallerSize] = smaller;
    const rest = Math.floor((total % size) / smallerSize);
    if (rest > 0) parts.push(`${rest} ${smallerUnit}`);
  }
  const text = parts.join(" ");
  return ms < 0 ? `${MINUS}${text}` : text;
};

const PRINTERS: Record<ValueFormat, (value: number) => string> = {
  count: formatCount,
  rate: formatRate,
  dollars: formatDollars,
  averageDollars: formatAverageDollars,
  duration: formatDuration,
};

const printSuppressed = (value: AnalyticsValue): PrintedValue => {
  const parts = suppressedPartsOf(value);
  const withheldOnly = parts.every((part) => part.reason === "complement");
  const text = withheldOnly ? WITHHELD : UNDER_FLOOR;
  return { text, label: text, reason: null };
};

const unmeasuredReason = (value: AnalyticsValue): string => {
  const given = (value as { reason?: unknown }).reason;
  if (typeof given === "string" && given.trim().length > 0) return given;
  return isRatio(value) ? ZERO_DENOMINATOR_REASON : NO_SOURCE_REASON;
};

/**
 * Prints one analytics value. A ratio always prints as a rate, from its two
 * printed parts; a suppressed part stops it before any division.
 */
export const formatAnalyticsValue = (
  value: AnalyticsValue,
  format: ValueFormat,
): PrintedValue => {
  if (isSuppressedValue(value)) return printSuppressed(value);
  if (isUnmeasuredValue(value)) {
    return { text: NOT_MEASURED, label: NOT_MEASURED, reason: unmeasuredReason(value) };
  }
  const printed = isRatio(value)
    ? formatRate(((value.numerator as number) / (value.denominator as number)) * 100)
    : PRINTERS[format]((value as MeasuredValue).value as number);
  const { approximate } = value as MeasuredValue | RatioValue;
  if (approximate !== true) return { text: printed, label: printed, reason: null };
  return { text: `~${printed}`, label: `about ${printed}`, reason: null };
};

const isTrueZero = (value: AnalyticsValue): boolean =>
  isRatio(value) ? value.numerator === 0 : (value as MeasuredValue).value === 0;

/**
 * The delta chip text, or null for no chip. The change is the server's; the
 * client never derives one. No chip when the server omitted it, when either
 * period is suppressed or unmeasured, or when both periods are a true zero.
 */
export const formatDeltaChip = ({
  kind,
  current,
  previous,
  change,
}: DeltaChipInput): string | null => {
  if (previous === null || typeof change !== "number" || !Number.isFinite(change)) return null;
  for (const value of [current, previous]) {
    if (isSuppressedValue(value) || isUnmeasuredValue(value)) return null;
  }
  if (isTrueZero(current) && isTrueZero(previous)) return null;
  return kind === "rate" ? formatRateChange(change) : formatCountChange(change);
};
